import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { FaFolder, FaFile, FaDownload, FaArrowLeft } from 'react-icons/fa';
import { 
    fetchEnValidationFiles, 
    fetchB2BValidationFiles, 
    fetchB2BRenewalFiles, 
    fetchCensusErrorFiles,
    fetchEmpyreanErrorFiles,
    getFileDownloadUrl, 
    downloadFile 
} from '../redux/actions/s3Actions';
import { AppDispatch, RootState } from '../redux/store';
import { S3File } from '../types/s3.types';
import styles from './FileAccess.module.css';

const FolderView: React.FC<{
    title: string;
    files: S3File[];
    currentPrefix: string;
    loading: boolean;
    error: string | null;
    onNavigate: (prefix: string) => void;
    onDownload: (file: S3File) => void;
    onBack: () => void;
}> = ({ title, files, currentPrefix, loading, error, onNavigate, onDownload, onBack }) => {
    return (
        <div className={styles.folderView}>
            <div className={styles.folderHeader}>
                <div className={styles.backButton} onClick={onBack}>
                    <FaArrowLeft /> Back to Folders
                </div>
                <h2>{title}</h2>
            </div>
            {loading && <div className={styles.loading}>Loading...</div>}
            {error && <div className={styles.error}>{error}</div>}
            {!loading && !error && (
                <div className={styles.fileList}>
                    {currentPrefix && (
                        <div className={styles.backButton} onClick={() => {
                            const newPrefix = currentPrefix.split('/').slice(0, -2).join('/') + '/';
                            onNavigate(newPrefix);
                        }}>
                            <FaArrowLeft /> Back
                        </div>
                    )}
                    {files.map((item, index) => (
                        <div key={index} className={styles.fileItem}>
                            {item.type === 'folder' ? (
                                <div className={styles.folderItem} onClick={() => onNavigate(item.path)}>
                                    <FaFolder className={styles.icon} />
                                    <span>{item.name}</span>
                                </div>
                            ) : (
                                <div className={styles.fileItem}>
                                    <div className={styles.fileInfo}>
                                        <FaFile className={styles.icon} />
                                        <span>{item.name}</span>
                                    </div>
                                    <button 
                                        className={styles.downloadButton}
                                        onClick={() => onDownload(item)}
                                    >
                                        <FaDownload />
                                    </button>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const MainFolderSection: React.FC<{
    title: string;
    folderName: string;
    onClick: () => void;
}> = ({ title, folderName, onClick }) => (
    <div className={styles.fileSection} onClick={onClick}>
        <h2>{title}</h2>
        <div className={styles.mainFolder}>
            <FaFolder className={styles.icon} />
            <span>{folderName}</span>
        </div>
    </div>
);

const FileAccess: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();
    const s3State = useSelector((state: RootState) => state.s3);
    const [activeSection, setActiveSection] = useState<string | null>(null);

    const handleFolderClick = (section: string) => {
        setActiveSection(section);
        switch (section) {
            case 'en':
                void dispatch(fetchEnValidationFiles(''));
                break;
            case 'b2b':
                void dispatch(fetchB2BValidationFiles(''));
                break;
            case 'renewal':
                void dispatch(fetchB2BRenewalFiles(''));
                break;
            case 'census':
                void dispatch(fetchCensusErrorFiles(''));
                break;
            case 'empyrean':
                void dispatch(fetchEmpyreanErrorFiles(''));
                break;
        }
    };

    const handleNavigate = (section: string, prefix: string) => {
        switch (section) {
            case 'en':
                void dispatch(fetchEnValidationFiles(prefix));
                break;
            case 'b2b':
                void dispatch(fetchB2BValidationFiles(prefix));
                break;
            case 'renewal':
                void dispatch(fetchB2BRenewalFiles(prefix));
                break;
            case 'census':
                void dispatch(fetchCensusErrorFiles(prefix));
                break;
            case 'empyrean':
                void dispatch(fetchEmpyreanErrorFiles(prefix));
                break;
        }
    };

    const handleDownload = (filePath: string) => {
        dispatch(downloadFile(filePath));
    };

    if (activeSection) {
        const sectionData = {
            en: {
                title: "EN Validation Error Files",
                state: s3State.enValidation
            },
            b2b: {
                title: "B2B Validation File Errors",
                state: s3State.b2bValidation
            },
            renewal: {
                title: "B2B Renewal Records",
                state: s3State.b2bRenewal
            },
            census: {
                title: "Census Uploader Error Files",
                state: s3State.censusErrors
            },
            empyrean: {
                title: "Empyrean Error Files",
                state: s3State.empyreanErrors
            }
        }[activeSection];

        if (!sectionData) {
            return <div className={styles.error}>Invalid section selected</div>;
        }

        return (
            <div>
                <div className={styles.backToDashboard} onClick={() => navigate('/dashboard')}>
                    <FaArrowLeft /> Back to Dashboard
                </div>
                <FolderView
                    title={sectionData.title}
                    files={sectionData.state.files}
                    currentPrefix={sectionData.state.currentPrefix}
                    loading={sectionData.state.loading}
                    error={sectionData.state.error}
                    onNavigate={(prefix) => handleNavigate(activeSection, prefix)}
                    onDownload={(file) => handleDownload(file.path)}
                    onBack={() => setActiveSection(null)}
                />
            </div>
        );
    }

    return (
        <div className={styles.fileAccess}>
            <div className={styles.backToDashboard} onClick={() => navigate('/dashboard')}>
                <FaArrowLeft /> Back to Dashboard
            </div>
            <h1>File Access</h1>
            <div className={styles.fileSections}>
                <MainFolderSection
                    title="EN Validation Error Files"
                    folderName="EN Error Files"
                    onClick={() => handleFolderClick('en')}
                />
                <MainFolderSection
                    title="B2B Validation File Errors"
                    folderName="B2B Error Files"
                    onClick={() => handleFolderClick('b2b')}
                />
                <MainFolderSection
                    title="B2B Renewal Records"
                    folderName="B2B Renewal Files"
                    onClick={() => handleFolderClick('renewal')}
                />
                <MainFolderSection
                    title="Census Uploader Error Files"
                    folderName="Census Error Files"
                    onClick={() => handleFolderClick('census')}
                />
                <MainFolderSection
                    title="Empyrean Error Files"
                    folderName="Empyrean Error Files"
                    onClick={() => handleFolderClick('empyrean')}
                />
            </div>
        </div>
    );
};

export default FileAccess;